import * as React from 'react';
import { type VariantProps, cva } from 'class-variance-authority';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@streetferret/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background',
  {
    variants: {
      variant: {
        default: 'bg-primary text-white/90 hover:bg-primary/90',
        ride: 'bg-ride text-ride-foreground hover:bg-ride/90',
        run: 'bg-run text-run-foreground hover:bg-run/90',
        slide: 'bg-slide text-slide-foreground hover:bg-slide/90',
        boat: 'bg-boat text-boat-foreground hover:bg-boat/90',
        swim: 'bg-swim text-swim-foreground hover:bg-swim/90',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'border border-input hover:bg-accent hover:text-accent-foreground',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'underline-offset-4 hover:underline text-foreground',
        contrast:
          'bg-background border border-input text-foreground hover:bg-secondary hover:text-secondary-foreground hover:border-muted-foreground',
        clear:
          'bg-transparent hover:backdrop-blur-lg border border-transparent hover:border-input transition-all',
        floating:
          'bg-background border drop-shadow-md text-foreground transition-colors hover:bg-secondary hover:text-secondary-foreground hover:border-muted-foreground',
      },
      size: {
        default: 'h-10 py-2 px-4',
        sm: 'h-9 px-3 rounded-md',
        lg: 'h-11 px-8 rounded-md',
        icon: 'px-1',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
